// shared-library/store/modules/funnel.js
// 创建一个变量来存储 API 实例的 getter 函数
let getApiInstance = null;

// 提供设置 API 实例 getter 的方法
export function setApiGetter(getter) {
  getApiInstance = getter;
}

// 获取当前 API 实例的辅助函数
function getApi() {
  if (!getApiInstance) {
    throw new Error('API getter not set. Please initialize the funnel module with an API instance.');
  }
  const api = getApiInstance();
  if (!api || !api.funnelWebApi) {
    throw new Error('Invalid API instance. Make sure the API object has funnelWebApi property.');
  }
  return api;
}

const state = {
  // 商机漏斗数据
  funnels: [],
  funnelsPageInfo: {},
  funnelChanges: [],
  funnelChangesPageInfo: {},
  funnelsOverDue: [],
  funnelsOverDuePageInfo: {},
  funnelsPastDue: [],
  funnelsPastDuePageInfo: {},
  // STORY-010（2026-10-21）：商机关闭-输单（cbmBusinessScheduleParentCode='CBS6' 的查询结果）
  funnelsClosedLost: [],
  funnelsClosedLostPageInfo: {},
  funnelLoading: false,
  funnelError: null
}

const getters = {
  // 商机漏斗数据
  funnels: state => state.funnels,
  funnelsPageInfo: state => state.funnelsPageInfo,
  funnelChanges: state => state.funnelChanges,
  funnelChangesPageInfo: state => state.funnelChangesPageInfo,
  funnelsOverDue: state => state.funnelsOverDue,
  funnelsOverDuePageInfo: state => state.funnelsOverDuePageInfo,
  funnelsPastDue: state => state.funnelsPastDue,
  funnelsPastDuePageInfo: state => state.funnelsPastDuePageInfo,
  // STORY-010：商机关闭-输单
  funnelsClosedLost: state => state.funnelsClosedLost,
  funnelsClosedLostPageInfo: state => state.funnelsClosedLostPageInfo,
  funnelLoading: state => state.funnelLoading,
  funnelError: state => state.funnelError
}

const actions = {
  /**
   * 获取所有商机漏斗数据
   */
  async fetchAllFunnels({ commit }, requestData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getAllFunnels(requestData);
      if (response.data.success) {
        commit('setFunnels', response.data.data);
        commit('setFunnelsPageInfo', response.data.pageInfo);
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机漏斗数据列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机漏斗数据列表失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },
  
  /**
   * 获取所有商机变更数据
   */
  async fetchAllFunnelChanges({ commit }, requestData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getAllFunnelChanges(requestData);
      if (response.data.success) {
        commit('setFunnelChanges', response.data.data);
        commit('setFunnelChangesPageInfo', response.data.pageInfo);
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机变更数据失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机变更数据失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 获取所有商机漏斗数据
   */
  async fetchAllFunnelsOverDue({ commit }, requestData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getAllFunnelsOverDue(requestData);
      if (response.data.success) {
        commit('setFunnelsOverDue', response.data.data);
        commit('setFunnelsOverDuePageInfo', response.data.pageInfo);
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机漏斗数据列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机漏斗数据列表失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },
  /**
   * 获取所有商机漏斗数据
   */
  async fetchAllFunnelsPastDue({ commit }, requestData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getAllFunnelsPastDue(requestData);
      if (response.data.success) {
        commit('setFunnelsPastDue', response.data.data);
        commit('setFunnelsPastDuePageInfo', response.data.pageInfo);
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机漏斗数据列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机漏斗数据列表失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * STORY-010（2026-10-21）：获取「商机关闭-输单」商机漏斗数据
   * ⚠️ 与 fetchAllFunnelsPastDue 同构，但走独立端点 /listClosedLost
   *    —— 上游在未传 cbmBusinessScheduleParentCode 时会硬排除 cbs_type_code != 'CBS6'，
   *       因此不能复用 list / listOverDue / listPastDue 的结果。
   */
  async fetchAllFunnelsClosedLost({ commit }, requestData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');

    try {
      const api = getApi();
      const response = await api.funnelWebApi.getAllFunnelsClosedLost(requestData);
      if (response.data.success) {
        commit('setFunnelsClosedLost', response.data.data);
        commit('setFunnelsClosedLostPageInfo', response.data.pageInfo);
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机漏斗数据列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机漏斗数据列表失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 推进商机
   * @param {object} context - Vuex上下文
   * @param {object} payload - 包含funnelId和pushData的对象
   */
  async pushFunnel({ commit }, pushData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.pushFunnel(pushData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '推进商机失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '推进商机失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 修改商机
   * @param {object} context - Vuex上下文
   * @param {object} payload - 包含funnelId和updateData的对象
   */
  async updateFunnel({ commit }, updateData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.updateFunnel(updateData);
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || '修改商机失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '修改商机失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 创建商机活动记录
   * @param {object} context - Vuex上下文
   * @param {object} payload - 包含活动数据的对象
   */
  async createFunnelActivity({ commit }, activityData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.createFunnelActivity(activityData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '创建商机活动记录失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '创建商机活动记录失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },
  
  /**
   * 获取商机详情
   * @param {object} context - Vuex上下文
   * @param {number} busMgmtId - 商机ID
   */
  async fetchFunnelDetail({ commit }, busMgmtId) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    
    try {
      const api = getApi();
      const response = await api.funnelWebApi.fetchFunnelDetail(busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机详情失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机详情失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  // ==================================================================
  // STORY-001 微信端新建商机页面（透传商机系统 tp* 接口）
  // ==================================================================

  /**
   * 新建商机（商机系统 tpSaveBusMgmt）
   * @param {object} context - Vuex上下文
   * @param {object} saveData - 商机数据（draftFlag=false 提交；true 暂存）
   * @returns {Promise<object>} 完整响应体（含 data + message），供页面解析新商机ID
   */
  async saveFunnel({ commit }, saveData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.saveFunnel(saveData);
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || '维护商机失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '维护商机失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 获取客户（医院）下拉选项（商机系统 tpGetCrmBusHospOptionList）
   */
  async getHospOptionList({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getHospOptionList(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取客户选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取客户选项失败');
      throw error;
    }
  },

  /**
   * 获取负责经销商下拉选项（商机系统 tpGetCrmBusOrgOptionList）
   */
  async getOrgOptionList({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getOrgOptionList(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取经销商选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取经销商选项失败');
      throw error;
    }
  },

  /**
   * 获取负责人下拉选项（商机系统 tpGetCrmBusOrgPersonOptionList）
   */
  async getOrgPersonOptionList({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getOrgPersonOptionList(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取负责人选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取负责人选项失败');
      throw error;
    }
  },

  /**
   * 获取登录人所属团队（骨科/外科）及其可选项（BUG-024）
   *
   * @returns {{orgPersonSection: string|null, orgPersonSectionOptions: Array}}
   */
  async getUserOrgPersonSection({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getUserOrgPersonSection(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取所属团队失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取所属团队失败');
      throw error;
    }
  },

  /**
   * 获取省份下拉选项（该用户授权省份，来自上游「商机系统」）（BUG-024）
   *
   * @returns {Array<{code: string, name: string}>}
   */
  async getBusProvinceOptionList({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getBusProvinceOptionList(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取省份选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取省份选项失败');
      throw error;
    }
  },

  /**
   * 按客户名称获取产品分类下拉选项（商机系统 tpGetCrmBusProdOptionList）
   */
  async getProdOptionList({ commit }, cbmHospName) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getProdOptionList(cbmHospName);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取产品分类选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取产品分类选项失败');
      throw error;
    }
  },

  /**
   * 保存商机产品明细行（商机系统 tpSaveCrmBusProd）
   */
  async saveCrmBusProd({ commit }, prodData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.saveCrmBusProd(prodData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存产品明细失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '保存产品明细失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 删除商机产品明细行（商机系统 tpDelCrmBusProd）
   */
  async delCrmBusProd({ commit }, { prodId, busMgmtId }) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.delCrmBusProd(prodId, busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '删除产品明细失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '删除产品明细失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 获取商机产品配置明细（商机系统 tpGetCrmBusProductConfigByBusProdId）
   */
  async getProdConfigByProdId({ commit }, prodId) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getProdConfigByProdId(prodId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取产品配置失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取产品配置失败');
      throw error;
    }
  },

  /**
   * 按字典编码获取单级选项（商机系统 tpListOptionsByCodeForOneLevel）
   */
  async listOptionsByCodeForOneLevel({ commit }, optionCode) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.listOptionsByCodeForOneLevel(optionCode);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取字典选项失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取字典选项失败');
      throw error;
    }
  },

  // ==================== STORY-004（2026-10-13）：微信端「草稿箱」 ====================

  /**
   * 获取商机草稿列表（商机系统 getBusMgmtDraftList）
   */
  async getBusMgmtDraftList({ commit }, requestData) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getBusMgmtDraftList(requestData || {});
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机草稿列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机草稿列表失败');
      throw error;
    }
  },

  /**
   * 获取商机草稿详情（商机系统 getBusMgmtDraftInfo）
   */
  async getBusMgmtDraftInfo({ commit }, busMgmtId) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getBusMgmtDraftInfo(busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机草稿详情失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机草稿详情失败');
      throw error;
    }
  },

  // ==================== STORY-006（2026-10-13）：微信端「商机新建页子信息 tabs」 ====================

  /**
   * 获取商机操作记录（字段级修改日志）
   */
  async getFunnelModifyLog({ commit }, busMgmtId) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getFunnelModifyLog(busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机操作记录失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机操作记录失败');
      throw error;
    }
  },

  /**
   * 获取商机跟进记录列表
   */
  async getFunnelActivityList({ commit }, busMgmtId) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getFunnelActivityList(busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机跟进记录列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机跟进记录列表失败');
      throw error;
    }
  },

  /**
   * 获取商机竞争态势列表
   */
  async getFunnelCompetitivList({ commit }, busMgmtId) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getFunnelCompetitivList(busMgmtId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取商机竞争态势列表失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取商机竞争态势列表失败');
      throw error;
    }
  },

  /**
   * 保存商机竞争态势
   */
  async saveFunnelCompetitiv({ commit }, competitivData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.saveFunnelCompetitiv(competitivData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存商机竞争态势失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '保存商机竞争态势失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 删除商机竞争态势
   */
  async delFunnelCompetitiv({ commit }, situId) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.delFunnelCompetitiv(situId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '删除商机竞争态势失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '删除商机竞争态势失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 获取医院信息（联系人/设备）
   */
  async getFunnelHosp({ commit }, crmBusHospAliasCode) {
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.getFunnelHosp(crmBusHospAliasCode);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取医院信息失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '获取医院信息失败');
      throw error;
    }
  },

  /**
   * 保存医院联系人
   */
  async saveFunnelHospContact({ commit }, contactData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.saveFunnelHospContact(contactData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存医院联系人失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '保存医院联系人失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 删除医院联系人
   */
  async delFunnelHospContact({ commit }, contactId) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.delFunnelHospContact(contactId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '删除医院联系人失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '删除医院联系人失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 保存医院设备
   */
  async saveFunnelHospEquip({ commit }, equipData) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.saveFunnelHospEquip(equipData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存医院设备失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '保存医院设备失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  /**
   * 删除医院设备
   */
  async delFunnelHospEquip({ commit }, equipId) {
    commit('setFunnelLoading', true);
    commit('clearFunnelError');
    try {
      const api = getApi();
      const response = await api.funnelWebApi.delFunnelHospEquip(equipId);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '删除医院设备失败');
      }
    } catch (error) {
      commit('setFunnelError', error.response?.data?.message || '删除医院设备失败');
      throw error;
    } finally {
      commit('setFunnelLoading', false);
    }
  },

  // ==================== STORY-009（2026-10-19）：微信端「推进材料」附件 ====================

  /**
   * 上传推进材料附件（商机系统 CGI uploadScheduleFile）
   *
   * ⚠️ 不占用全局 funnelLoading（避免整个页面 loading），由页面用局部状态控制按钮 loading。
   * @returns {{filePath: string, fileName: string, size: number}}
   */
  async uploadScheduleFile({ commit }, payload) {
    commit('clearFunnelError');
    const api = getApi();
    const response = await api.funnelWebApi.uploadScheduleFile(payload);
    if (response.data.success) {
      return response.data.data;
    }
    // BP-007：抛出的错误必须带对端真实原因，供页面直接展示
    throw new Error(response.data.message || '上传推进材料失败');
  },

  /**
   * 删除推进材料附件（商机系统 CGI delScheduleFile）
   */
  async delScheduleFile({ commit }, csfId) {
    commit('clearFunnelError');
    const api = getApi();
    const response = await api.funnelWebApi.delScheduleFile(csfId);
    if (response.data.success) {
      return true;
    }
    throw new Error(response.data.message || '删除推进材料失败');
  },

  /**
   * 下载推进材料附件（商机系统 CGI downloadScheduleFile）
   *
   * ⚠️ 返回的是 blob（responseType='blob'），由页面保存为文件。
   */
  async downloadScheduleFile({ commit }, csfId) {
    commit('clearFunnelError');
    const api = getApi();
    return api.funnelWebApi.downloadScheduleFile(csfId);
  }
}

const mutations = {
  // 商机漏斗数据
  setFunnels(state, funnels) {
    state.funnels = funnels;
  },

  setFunnelsPageInfo(state, pageInfo) {
    state.funnelsPageInfo = pageInfo;
  },

  setFunnelChanges(state, changes) {
    state.funnelChanges = changes;
  },

  setFunnelChangesPageInfo(state, pageInfo) {
    state.funnelChangesPageInfo = pageInfo;
  },

  setFunnelsOverDue(state, funnels) {
    state.funnelsOverDue = funnels;
  },

  setFunnelsOverDuePageInfo(state, pageInfo) {
    state.funnelsOverDuePageInfo = pageInfo;
  },

  setFunnelsPastDue(state, funnels) {
    state.funnelsPastDue = funnels;
  },

  setFunnelsPastDuePageInfo(state, pageInfo) {
    state.funnelsPastDuePageInfo = pageInfo;
  },

  // STORY-010（2026-10-21）：商机关闭-输单
  setFunnelsClosedLost(state, funnels) {
    state.funnelsClosedLost = funnels;
  },

  setFunnelsClosedLostPageInfo(state, pageInfo) {
    state.funnelsClosedLostPageInfo = pageInfo;
  },

  setFunnelLoading(state, status) {
    state.funnelLoading = status;
  },
  
  setFunnelError(state, error) {
    state.funnelError = error;
  },
  
  clearFunnelError(state) {
    state.funnelError = null;
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}